/**
 * Phase 1 — deterministic filesystem probe for `aaac init --bootstrap`.
 */

import { existsSync, readdirSync } from "node:fs";
import { readFile, readdir } from "node:fs/promises";
import { join, resolve } from "node:path";

const SOURCE_DIR_CANDIDATES = ["src", "lib", "app", "packages"] as const;
const TEST_DIR_CANDIDATES = ["__tests__", "tests", "test"] as const;
const DOC_DIR_CANDIDATES = ["docs", "doc"] as const;

const CONFIG_FILES = {
  artifactContracts: ["artifact-contracts.yaml", "artifact-contracts.yml"],
  agentContracts: ["agent-contracts.yaml", "agent-contracts.yml"],
  cliContract: ["cli-contract.yaml", "cli-contract.yml"],
  microContracts: ["micro-contracts.yaml", "micro-contracts.yml"],
  toolchainContracts: ["toolchain-contracts.yaml", "toolchain-contracts.yml"],
} as const;

export interface PackageJsonProbe {
  name?: string;
  scripts: Record<string, string>;
  workspaces?: string[] | { packages: string[] };
}

export interface ProbeResult {
  projectRoot: string;
  packageJson?: PackageJsonProbe;
  isMonorepo: boolean;
  workspacePackages: string[];
  sourceDirs: string[];
  testDirs: string[];
  docDirs: string[];
  hasTsconfig: boolean;
  language: "typescript" | "javascript" | "unknown";
  existingConfigs: {
    artifactContracts?: string;
    agentContracts?: string;
    cliContract?: string;
    microContracts?: string;
    toolchainContracts?: string;
    componentContracts: string[];
    projectYaml: boolean;
  };
}

function findFirstExisting(root: string, candidates: readonly string[]): string | undefined {
  for (const name of candidates) {
    if (existsSync(join(root, name))) return name;
  }
  return undefined;
}

function detectWorkspaces(pkg: PackageJsonProbe | undefined): string[] {
  if (!pkg?.workspaces) return [];
  if (Array.isArray(pkg.workspaces)) return pkg.workspaces;
  return pkg.workspaces.packages ?? [];
}

async function findComponentContracts(root: string): Promise<string[]> {
  const found: string[] = [];
  if (existsSync(join(root, "component.yaml"))) {
    found.push("component.yaml");
  }

  let entries: string[];
  try {
    entries = await readdir(root);
  } catch {
    return found;
  }

  for (const entry of entries) {
    if (entry.endsWith("-contract.yaml") || entry.endsWith("-contract.yml")) {
      found.push(entry);
    }
  }
  return found;
}

export async function probeProject(projectRoot: string): Promise<ProbeResult> {
  const root = resolve(projectRoot);
  let packageJson: PackageJsonProbe | undefined;

  const pkgPath = join(root, "package.json");
  if (existsSync(pkgPath)) {
    try {
      const parsed = JSON.parse(await readFile(pkgPath, "utf8")) as {
        name?: string;
        scripts?: Record<string, string>;
        workspaces?: string[] | { packages: string[] };
      };
      packageJson = {
        name: parsed.name,
        scripts: parsed.scripts ?? {},
        workspaces: parsed.workspaces,
      };
    } catch {
      packageJson = { scripts: {} };
    }
  }

  const workspacePackages = detectWorkspaces(packageJson);
  const isMonorepo = workspacePackages.length > 0;

  const sourceDirs = SOURCE_DIR_CANDIDATES.filter((d) => existsSync(join(root, d)));
  const testDirs = TEST_DIR_CANDIDATES.filter((d) => existsSync(join(root, d)));
  const docDirs = DOC_DIR_CANDIDATES.filter((d) => existsSync(join(root, d)));

  const hasTsconfig =
    existsSync(join(root, "tsconfig.json")) ||
    existsSync(join(root, "tsconfig.base.json"));

  let language: ProbeResult["language"] = "unknown";
  if (hasTsconfig) {
    language = "typescript";
  } else if (sourceDirs.some((d) => {
    try {
      return readdirSync(join(root, d)).some((f) => f.endsWith(".ts") || f.endsWith(".tsx"));
    } catch {
      return false;
    }
  })) {
    language = "typescript";
  } else if (existsSync(pkgPath)) {
    language = "javascript";
  }

  const componentContracts = await findComponentContracts(root);

  return {
    projectRoot: root,
    packageJson,
    isMonorepo,
    workspacePackages,
    sourceDirs,
    testDirs,
    docDirs,
    hasTsconfig,
    language,
    existingConfigs: {
      artifactContracts: findFirstExisting(root, CONFIG_FILES.artifactContracts),
      agentContracts: findFirstExisting(root, CONFIG_FILES.agentContracts),
      cliContract: findFirstExisting(root, CONFIG_FILES.cliContract),
      microContracts: findFirstExisting(root, CONFIG_FILES.microContracts),
      toolchainContracts: findFirstExisting(root, CONFIG_FILES.toolchainContracts),
      componentContracts,
      projectYaml: existsSync(join(root, "project.yaml")),
    },
  };
}
